import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDisseminationLessonQuery } from "./hooks/useDisseminationLessonQuery";
import { useDisseminationLessonCommand } from "./hooks/useDisseminationLessonCommand";
import { ROUTES } from "../../core/routes/routes";
import { DateUtil } from "../../common/utils/date.util";
import Table from "../../common/components/Table";
import Pagination from "../../common/components/Pagination";
import IconButton from "../../common/components/IconButton";
import Textfield from "../../common/components/Textfiled";
import { BsFileSpreadsheet, BsClipboardPlus, BsFilePdf, BsPencilSquare, BsTrash } from "react-icons/bs";


const columns = [
  {
    header: "Nombre del involucrado",
    accessor: "reportAccident.namePersonInvolved",
    render: (_, row) =>
      row.reportAccident?.namePersonInvolved || "No disponible",
  },
  {
    header: "Documento",
    accessor: "reportAccident.documentPersonInvolved",
    render: (_, row) =>
      row.reportAccident?.documentPersonInvolved || "No disponible",
  },
  {
    header: "Placa del vehículo",
    accessor: "reportAccident.vehiclePlate",
    render: (_, row) => row.reportAccident?.vehiclePlate || "No disponible",
  },
  {
    header: "Tipo de contrato",
    accessor: "reportAccident.typeContract",
    render: (_, row) => row.reportAccident?.typeContract || "No disponible",
  },
  {
    header: "Lugar del evento",
    accessor: "reportAccident.placeOfEvent",
    render: (_, row) => row.reportAccident?.placeOfEvent || "No disponible",
  },
  {
    header: "Fecha del siniestro",
    accessor: "reportAccident.dateAccident",
    render: (_, row) =>
      row.reportAccident?.dateAccident
        ? DateUtil.format(row.reportAccident.dateAccident)
        : "No disponible",
  },
  {
    header: "Fecha de creación",
    accessor: "createdAt",
    render: (val) => DateUtil.format(val),
  },
  {
    header: "Fecha de actualización",
    accessor: "updateAt",
    render: (val) => DateUtil.format(val),
  },
  {
    header: "Lección aprendida",
    accessor: "lesson",
    render: (val) =>
      val ? (
        <a href={val} target="_blank" rel="noopener noreferrer">
          Ver PDF
        </a>
      ) : (
        "No disponible"
      ),
  },
];

function ListDisseminationLesson() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const accidentId = searchParams.get("accidentId");
  const [filters, setFilters] = useState({
    search: "",
    startDate: "",
    endDate: "",
    ...(accidentId ? { accidentId } : {}),
  });
  const {
    disseminationLessons,
    totalPages,
    loading,
    onChangePage,
    currentPage,
    refresh,
  } = useDisseminationLessonQuery(filters);
  const {
    exportToExcel,
    exportToPdf,
    deleteDisseminationLesson,
    loading: loadingAction,
  } = useDisseminationLessonCommand(refresh);

  const columnsWithActions = [
    ...columns,
    {
      header: "Acciones",
      accessor: "actions",
      render: (_, row) => (
        <div className="d-flex align-items-center gap-2">
          <IconButton
            tooltipText="Editar"
            onClick={() =>
              navigate(ROUTES.REGISTER_DISSEMINATION_LESSON, { state: { editData: row } })
            }
          >
            <BsPencilSquare size={20} />
          </IconButton>
          <IconButton
            tooltipText="Eliminar"
            className="text-danger"
            onClick={() => deleteDisseminationLesson(row.id)}
          >
            <BsTrash size={20} />
          </IconButton>
        </div>
      ),
    },
  ];

  return (
    <main className="container-fluid p-4">
      <header className="d-flex justify-content-between align-items-center mb-4 px-3 py-2 border-bottom">
        <h1 className="h2 m-0 fw-bold">Lista de lecciones</h1>
        <div className="d-flex gap-2">
          <div>
            <IconButton tooltipText="Registrar lección aprendida"
              onClick={() => navigate(ROUTES.REGISTER_DISSEMINATION_LESSON)}
            >
              <BsClipboardPlus size={24} />
            </IconButton>
          </div>
          <div>
            <IconButton
              tooltipText="Exportar a Excel"
              loading={loadingAction}
              onClick={() => exportToExcel(filters)}
            >
              <BsFileSpreadsheet size={24} />
            </IconButton>
          </div>
          <div>
            <IconButton
              tooltipText="Exportar a PDF"
              onClick={() => exportToPdf(filters)}
              loading={loadingAction}
            >
              <BsFilePdf size={24} />
            </IconButton>
          </div>
        </div>
      </header>

      <section className="shadow w-100 d-flex flex-column">
        <div className="d-flex gap-3 flex-wrap flex-md-nowrap">
          <div className="w-100">
            <Textfield
              label="Buscar"
              name="search"
              onChange={({ value }) => setFilters({ search: value })}
              placeholder="Buscar..."
              value={filters.search}
            />
          </div>

          <div className="d-flex gap-3 flex-wrap flex-md-nowrap justify-content-center">
            <Textfield
              label="Fecha inicio"
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={({ value }) =>
                setFilters((prev) => ({ ...prev, startDate: value }))
              }
            />
            <Textfield
              label="Fecha fin"
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={({ value }) =>
                setFilters((prev) => ({ ...prev, endDate: value }))
              }
            />
          </div>
        </div>

        <Table
          columns={columnsWithActions}
          data={disseminationLessons}
          showIndex={true}
          loading={loading}
        />

        <Pagination
          onChangePage={onChangePage}
          page={currentPage}
          totalPages={totalPages}
        />
      </section>
    </main>
  );
}

export default ListDisseminationLesson;
